import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Suspense, useMemo, useState, useTransition } from "react";
import { relocationViewCountListQuery } from "@/app/Relocations/GraphQL/relocationViewCountListQuery";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import dayjs from "dayjs";
import { Badge } from "@/components/catalyst/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { dealScoreTone } from "@/app/Relocations/Utils/dealScoreTone";
import { formatStatistic } from "@/lib/Formatters/formatStatistic";
import { formatPercent } from "@/lib/Formatters/formatPercent";
import { cn } from "@/lib/utils";
import { useRelocation } from "@/app/Relocations/Hooks/useRelocation";
import {
  durationData,
  getRelocationViewsVariables,
  TimePeriod,
} from "@/app/Relocations/Utils/getRelocationViewsVariables";
import { useTranslations } from "use-intl";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationDealChatDialog } from "@/app/Relocations/Components/RelocationDealChatDialog";

export function RelocationStats() {
  const relocation = useRelocationRecord();

  return (
    <Suspense fallback={<Skeleton className="h-[453px] w-full" />}>
      <Component relocationId={relocation.id} />
    </Suspense>
  );
}

export function Component({ relocationId }: { relocationId: string }) {
  const t = useTranslations("relocation.stats");
  const relocation = useRelocation(relocationId);
  const { open: openDealChat } = useDialog(RelocationDealChatDialog);

  const chartConfig = {
    index: {
      label: t("relocation"),
      color: "hsl(var(--chart-2))",
    },
  } satisfies ChartConfig;

  const [duration, setDuration] = useState<TimePeriod>("week");
  const [isPending, startTransition] = useTransition();
  const variables = getRelocationViewsVariables(relocationId, duration);
  const { periods, unit } = durationData[duration];

  const { data } = useSuspenseGqlQuery(relocationViewCountListQuery, variables);

  const chartData = useMemo(
    () =>
      Array.from({ length: periods }, (_, i) => {
        const date = dayjs(variables.start).add(i + 1, unit);

        //Find the data for the date
        const relocationData = data?.relocationViews.relocation.filter((r) => {
          const dataDate = dayjs(r.hour, {
            utc: true,
          });

          return dataDate.isSame(date, "date");
        });

        //Find the data for the date
        const regionData = data?.relocationViews.region_average.filter((r) => {
          const dataDate = dayjs(r.hour, {
            utc: true,
          });

          return dataDate.isSame(date, "date");
        });

        return {
          time: date.clone().local().startOf(unit).format("YYYY-MM-DD HH:mm"),
          region: regionData.reduce((acc, curr) => {
            return acc + curr.view_count;
          }, 0),
          relocation: relocationData.reduce((acc, curr) => {
            return acc + curr.view_count;
          }, 0),
        };
      }),
    [data, periods, unit, variables.start],
  );

  // Raw hourly counts are too small to read; index the running total against the
  // region's running average so 100 always means "level with the region".
  const indexedData = useMemo(() => {
    let relocationTotal = 0;
    let regionTotal = 0;

    return chartData.map(({ time, region, relocation }) => {
      relocationTotal += relocation;
      regionTotal += region;

      return {
        time,
        index: regionTotal > 0 ? (relocationTotal / regionTotal) * 100 : null,
      };
    });
  }, [chartData]);

  const dealScore = relocation.deal_score ?? null;

  return (
    <Card
      className={cn({
        "animate-pulse": isPending,
      })}
    >
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>{t("listingPerformance")}</CardTitle>
          <CardDescription>
            {t("showingVisitors")}{" "}
            <select
              className="border-none text-sm"
              value={duration}
              onChange={(e) => {
                startTransition(() => {
                  setDuration(e.target.value as any);
                });
              }}
            >
              <option value="week">{t("last7Days")}</option>
              <option value="month">{t("last30Days")}</option>
            </select>
          </CardDescription>
        </div>
        <div className="flex flex-wrap">
          <button
            type="button"
            onClick={() => openDealChat({ relocationId })}
            className="data-[active=true]:bg-muted/50 relative z-30 flex min-w-36 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l enabled:hover:bg-zinc-50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6"
          >
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              {t("rating")}
              <Badge color="purple">beta</Badge>
            </span>
            <span
              className={cn(
                "text-lg font-bold leading-none sm:text-3xl",
                dealScore !== null ? dealScoreTone(dealScore).text : null,
              )}
            >
              {dealScore !== null ? `${dealScore}/10` : "—"}
            </span>
          </button>
          <div className="data-[active=true]:bg-muted/50 relative z-30 flex min-w-36 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
            <span className="text-xs text-gray-500">{t("views")}</span>
            <span className="whitespace-nowrap text-lg font-bold leading-none sm:text-3xl">
              {formatStatistic(relocation.stats?.views_total)}
            </span>
          </div>
          <div className="data-[active=true]:bg-muted/50 relative z-30 flex min-w-36 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
            <span className="whitespace-nowrap text-xs text-gray-500">
              {t("expiringIn")}
            </span>
            <span className="whitespace-nowrap text-lg font-bold leading-none sm:text-3xl">
              {relocation.stats?.days_until_expiry
                ? t("daysCount", { count: relocation.stats?.days_until_expiry })
                : "--"}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <AreaChart
            accessibilityLayer
            data={indexedData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                return formatLabel(value);
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={48}
              domain={[0, (max: number) => Math.max(120, Math.ceil(max))]}
              tickFormatter={(value: number) => `${Math.round(value)}%`}
            />
            <ReferenceLine
              y={100}
              label={{ value: t("regionAvg"), position: "insideTopLeft" }}
              stroke="hsl(var(--chart-1))"
              strokeDasharray="4 4"
            />
            <ReferenceLine
              label={t("listed")}
              x={dayjs(relocation.created_at, {
                utc: true,
              })
                .local()
                .startOf(unit)
                .format("YYYY-MM-DD HH:mm")}
              stroke="red"
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(val) => {
                    return formatLabel(val);
                  }}
                  formatter={(value) => (
                    <span className="text-muted-foreground">
                      {t("percentOfRegionAvg", {
                        percent: formatPercent(Number(value) / 100),
                      })}
                    </span>
                  )}
                />
              }
            />
            <Area
              dataKey="index"
              type="monotone"
              connectNulls={false}
              fill="var(--color-index)"
              fillOpacity={0.1}
              stroke="var(--color-index)"
              strokeWidth={2}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

function formatLabel(date: string) {
  return dayjs(date).format("MMM D");
}
